import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ExactRateLimit } from '../../api/exactConnection';
import { formatDuration } from '../../api/queueMonitor';

export interface IExactRateLimitBarProps {
    rateLimit: ExactRateLimit | null;
}

/** How often the countdown to the reset is redrawn. Same tick as the token countdown. */
const TICK_MS = 1000;

/**
 * From when what is left is worth colouring.
 *
 * Two thresholds and not three. Exact hands out thousands of calls a day and a connector
 * that is behaving uses a fraction of them, so the interesting question is not "how full is
 * it" but "is this going to run out today" - and that only becomes a question in the last
 * quarter. Above it the bar stays neutral, so a coloured bar means something.
 */
const WARN_BELOW = 0.25;

const BAD_BELOW = 0.1;

const MEASURED_HINT =
    'Wanneer deze stand is opgeschreven. De connector schrijft hem na elke aanroep van Exact, dus dit is meteen het moment waarop er voor het laatst iets aan Exact is gevraagd. Is dat lang geleden, dan zegt het getal hierboven niets over nu.';

const LIMIT_HINT =
    'Exact telt aanroepen per app, per administratie, per 24 uur. Is het op, dan beantwoordt Exact alles met 429 tot de reset - wat er hier uitziet als een verzendfunction die opeens op elk object stukloopt.';

const RESET_HINT =
    'Wanneer het venster van 24 uur omslaat en de teller opnieuw begint. Dit moment komt van Exact zelf, uit dezelfde header als de aantallen.';

/** Whole numbers, in the notation the rest of the dashboard uses. */
const count = (value: number): string => value.toLocaleString('nl-NL');

/** A moment as a clock time, with the date only when it is not today. */
const clock = (iso?: string | null, now: number = Date.now()): string | null => {
    if (!iso) {
        return null;
    }

    const parsed = Date.parse(iso);

    if (Number.isNaN(parsed)) {
        return null;
    }

    const moment = new Date(parsed);
    const time = moment.toLocaleTimeString('nl-NL', {
        hour: '2-digit',
        minute: '2-digit',
    });

    return moment.toDateString() === new Date(now).toDateString()
        ? time
        : `${moment.toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'short',
          })} ${time}`;
};

/**
 * What is left of the daily Exact API budget, as a bar.
 *
 * The one number about Exact that nothing on this screen used to show, and the one that
 * explains a class of failure nothing else does: a send handler that starts failing on
 * every object at once, with a 429 underneath it. That is not a data problem and retrying
 * makes it worse - the retries spend the next window on the same objects - so telling it
 * apart from an ordinary failure is the difference between waiting and digging.
 *
 * Read back rather than measured, and the bar says so. The numbers come off the log line
 * the connector writes after every Exact call, so they are exactly as fresh as that call:
 * an environment that last talked to Exact this morning has a bar from this morning, and
 * the moment underneath it is not a footnote but the thing that decides how much weight to
 * put on the rest.
 */
export const ExactRateLimitBar = ({ rateLimit }: IExactRateLimitBarProps) => {
    const [now, setNow] = useState<number>(() => Date.now());

    const resetAt = rateLimit?.resetAt;

    useEffect(() => {
        if (!resetAt) {
            return;
        }

        const timer = setInterval(() => setNow(Date.now()), TICK_MS);

        return () => clearInterval(timer);
    }, [resetAt]);

    /** The fraction still available, or null when the two numbers do not both exist. */
    const left = useMemo(() => {
        const limit = rateLimit?.limit ?? 0;
        const remaining = rateLimit?.remaining;

        if (limit <= 0 || remaining === null || remaining === undefined) {
            return null;
        }

        return Math.min(1, Math.max(0, remaining / limit));
    }, [rateLimit?.limit, rateLimit?.remaining]);

    const tone =
        left === null
            ? 'unknown'
            : left <= BAD_BELOW
            ? 'bad'
            : left <= WARN_BELOW
            ? 'warn'
            : 'ok';

    /** How long until the window rolls over; negative once it already has. */
    const untilReset = useMemo(() => {
        if (!resetAt) {
            return null;
        }

        const parsed = Date.parse(resetAt);

        return Number.isNaN(parsed) ? null : (parsed - now) / 1000;
    }, [resetAt, now]);

    if (!rateLimit) {
        return null;
    }

    /*
        Nothing to report is a state of its own, and a sentence rather than an empty bar.
        There are two ways into it and they mean opposite things - an environment that has
        not called Exact in two days, and a log this dashboard could not read - so the line
        says which of the two it is instead of leaving a gap.
    */
    if (!rateLimit.found) {
        return (
            <div className="exact-limit exact-limit--empty">
                <span className="exact-limit__title" title={LIMIT_HINT}>
                    Exact API-limiet
                </span>

                <p className="exact-limit__none">
                    {rateLimit.error ??
                        `Geen meting in het logboek van de afgelopen ${rateLimit.daysSearched} dagen. De connector schrijft die stand alleen weg als hij Exact aanroept, dus dit betekent meestal dat er niets naar Exact is gegaan.`}
                </p>

                {rateLimit.line && (
                    <code className="exact-limit__raw">{rateLimit.line}</code>
                )}
            </div>
        );
    }

    const measured = clock(rateLimit.measuredAt, now);
    const reset = clock(rateLimit.resetAt, now);

    const usedShare =
        left === null ? 0 : Math.round((1 - left) * 100);

    return (
        <div className={`exact-limit exact-limit--${tone}`}>
            <div className="exact-limit__head">
                <span className="exact-limit__title" title={LIMIT_HINT}>
                    Exact API-limiet
                </span>

                <span className="exact-limit__numbers">
                    {rateLimit.remaining !== null &&
                    rateLimit.remaining !== undefined &&
                    rateLimit.limit
                        ? `${count(rateLimit.remaining)} van ${count(
                              rateLimit.limit
                          )} over`
                        : rateLimit.remaining !== null &&
                          rateLimit.remaining !== undefined
                        ? `${count(rateLimit.remaining)} over`
                        : 'aantallen onbekend'}
                </span>
            </div>

            {/*
                The bar shows what is gone rather than what is left, filling from the left
                like every other progress bar on this dashboard. Both numbers are in the
                label above it, so the direction is a matter of reading habit rather than of
                information - and a bar that empties from the left reads as progress.
            */}
            <div
                className="exact-limit__track"
                role="progressbar"
                aria-valuenow={usedShare}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Verbruikt deel van de dagelijkse Exact API-limiet"
                title={
                    rateLimit.used !== null && rateLimit.used !== undefined
                        ? `${count(rateLimit.used)} aanroepen verbruikt (${usedShare}%).`
                        : LIMIT_HINT
                }
            >
                <span
                    className="exact-limit__fill"
                    style={{ width: `${usedShare}%` }}
                />
            </div>

            <div className="exact-limit__facts">
                {reset && (
                    <span title={RESET_HINT}>
                        <FontAwesomeIcon
                            icon="rotate"
                            className="exact-limit__icon"
                        />
                        reset om {reset}
                        {untilReset !== null &&
                            untilReset > 0 &&
                            ` · over ${formatDuration(untilReset) ?? 'even'}`}
                        {untilReset !== null &&
                            untilReset <= 0 &&
                            ' · dat moment is voorbij, de eerstvolgende aanroep laat de nieuwe stand zien'}
                    </span>
                )}

                {measured && (
                    <span title={MEASURED_HINT}>
                        <FontAwesomeIcon
                            icon="clock"
                            className="exact-limit__icon"
                        />
                        gemeten om {measured}
                    </span>
                )}

                {rateLimit.call && (
                    <span title="De aanroep waar deze stand vanaf komt. Dat zegt iets over waar het budget heen gaat: een timer die vorderingen bijwerkt is iets anders dan één object dat blijft hangen in nieuwe pogingen.">
                        <FontAwesomeIcon
                            icon="paper-plane"
                            className="exact-limit__icon"
                        />
                        <code>{rateLimit.call}</code>
                    </span>
                )}
            </div>

            {tone === 'bad' && (
                <p className="exact-limit__warning">
                    Het budget is bijna op. Zodra het op is beantwoordt Exact
                    alles met 429; die berichten komen terug op de queue en
                    worden opnieuw geprobeerd, dus dan gaat het volgende venster
                    op aan dezelfde objecten. Zet de verzending liever stil tot
                    de reset.
                </p>
            )}
        </div>
    );
};
